import { useAtomValue } from "jotai";
import type { JSX } from "react";

import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Input } from "@frontend/components/atoms/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { Switch } from "@frontend/components/atoms/Switch";
import { useField } from "@frontend/hooks/form";
import { useLingui } from "@lingui/react/macro";

import { clientForm, optionsAtom } from "../stores/editor";
import { Section } from "./Section";

const fields = clientForm.fields;

export function IdentitySection(): JSX.Element {
  const { t } = useLingui();

  const options = useAtomValue(optionsAtom);

  const isOrganization = useField(fields.isOrganization);
  const organization = useField(fields.organization);
  const organizationSubName = useField(fields.organizationSubName);
  const title = useField(fields.title);
  const firstName = useField(fields.firstName);
  const lastName = useField(fields.lastName);

  const company = isOrganization.value ?? false;

  const titles = withCurrent(options.titles, title.value);

  return (
    <Section icon="IdCard" legend={t`Identity`}>
      <Field>
        <FieldLabel>{t`Company`}</FieldLabel>

        <FieldContent>
          <Switch
            checked={company}
            onCheckedChange={(next) => isOrganization.setValue(next)}
          />
        </FieldContent>
      </Field>

      {company ? (
        <>
          <Field invalid={Boolean(organization.error)}>
            <FieldLabel>{t`Name`}</FieldLabel>

            <FieldContent>
              <Input
                value={organization.value ?? ""}
                onValueChange={(next) => organization.setValue(next)}
              />

              <FieldError match={Boolean(organization.error)}>
                {organization.error}
              </FieldError>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>{t`Name complement`}</FieldLabel>

            <FieldContent>
              <Input
                value={organizationSubName.value ?? ""}
                onValueChange={(next) => organizationSubName.setValue(next)}
              />
            </FieldContent>
          </Field>
        </>
      ) : (
        <>
          <Field>
            <FieldLabel>{t`Title`}</FieldLabel>

            <FieldContent>
              <Select
                items={titles.map((label) => ({ label, value: label }))}
                value={title.value ?? null}
                onValueChange={(next) => title.setValue(next)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t`Please choose an option`} />
                </SelectTrigger>

                <SelectContent>
                  {titles.map((label) => (
                    <SelectItem key={label} value={label}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldContent>
          </Field>

          <Field invalid={Boolean(lastName.error)}>
            <FieldLabel>{t`Last name`}</FieldLabel>

            <FieldContent>
              <Input
                value={lastName.value ?? ""}
                onValueChange={(next) => lastName.setValue(next)}
              />

              <FieldError match={Boolean(lastName.error)}>
                {lastName.error}
              </FieldError>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>{t`First name`}</FieldLabel>

            <FieldContent>
              <Input
                value={firstName.value ?? ""}
                onValueChange={(next) => firstName.setValue(next)}
              />
            </FieldContent>
          </Field>
        </>
      )}
    </Section>
  );
}

function withCurrent(
  titles: string[],
  current: string | null | undefined,
): string[] {
  if (null == current || "" === current || titles.includes(current)) {
    return titles;
  }

  return [current, ...titles];
}
